/**
 * VÉRAULT TRAVELS — Discover Places Component
 * 
 * Search real restaurants, attractions, museums, etc. near trip destinations.
 * Powered by OpenStreetMap Overpass API (free, no key required).
 */

import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import {
  Search, MapPin, Utensils, Coffee, Wine, Landmark, TreePine,
  ShoppingBag, Waves, Music, Store, Hotel, Sparkles, Loader2,
  ExternalLink, Phone, Clock, Plus, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  restaurants: Utensils,
  cafes: Coffee,
  bars: Wine,
  attractions: Landmark,
  museums: Landmark,
  parks: TreePine,
  shopping: ShoppingBag,
  hotels: Hotel,
  spas: Sparkles,
  beaches: Waves,
  nightlife: Music,
  markets: Store,
};

export function DiscoverPlaces({ destination, tripId, onAddToItinerary }) {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('restaurants');
  const [customDest, setCustomDest] = useState('');
  const [searchedDest, setSearchedDest] = useState('');
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [categories] = useState([
    { id: 'restaurants', label: 'Restaurants' },
    { id: 'cafes', label: 'Cafes' },
    { id: 'bars', label: 'Bars' },
    { id: 'attractions', label: 'Attractions' },
    { id: 'museums', label: 'Museums' },
    { id: 'parks', label: 'Parks' },
    { id: 'shopping', label: 'Shopping' },
    { id: 'spas', label: 'Spas' },
    { id: 'nightlife', label: 'Nightlife' },
    { id: 'markets', label: 'Markets' },
  ]);

  const handleSearch = useCallback(async (cat) => {
    const dest = customDest || destination;
    if (!dest) {
      toast.error('Enter a destination to search');
      return;
    }
    setLoading(true);
    setCategory(cat || category);
    try {
      const res = await axios.get(`${API}/places/search`, {
        params: { destination: dest, category: cat || category, radius: 5000, limit: 15 }
      });
      setPlaces(res.data.places || []);
      setTotal(res.data.total || 0);
      setSearchedDest(dest);
    } catch (e) {
      toast.error('Failed to search places');
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  }, [customDest, destination, category]);

  const IconForCategory = CATEGORY_ICONS[category] || MapPin;

  return (
    <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6" data-testid="discover-places">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-4"
      >
        <h3 className="font-medium text-[#1A1A1A] flex items-center gap-2">
          <Search className="w-5 h-5 text-[#C8A85A]" />
          Discover Places
        </h3>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#8C8C8C]" /> : <ChevronDown className="w-4 h-4 text-[#8C8C8C]" />}
      </button>

      {expanded && (
        <div className="space-y-4">
          <p className="text-sm text-[#8C8C8C]">
            Search real restaurants, attractions, and activities near your destinations. Powered by OpenStreetMap.
          </p>

          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
              <Input
                value={customDest}
                onChange={(e) => setCustomDest(e.target.value)}
                placeholder={destination || 'Enter destination...'}
                className="pl-10 bg-[#F5F1EB]"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                data-testid="places-destination-input"
              />
            </div>
            <Button
              onClick={() => handleSearch()}
              disabled={loading}
              className="bg-[#C8A85A] hover:bg-[#B39049] text-white"
              data-testid="places-search-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-1.5">
            {categories.map(cat => {
              const CatIcon = CATEGORY_ICONS[cat.id] || MapPin;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setCategory(cat.id); handleSearch(cat.id); }}
                  className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 transition-colors ${
                    category === cat.id
                      ? 'bg-[#C8A85A] text-white'
                      : 'bg-[#F5F1EB] text-[#595959] hover:bg-[#EEE9E2]'
                  }`}
                  data-testid={`places-cat-${cat.id}`}
                >
                  <CatIcon className="w-3.5 h-3.5" />
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#C8A85A]" />
            </div>
          ) : places.length > 0 ? (
            <div>
              <p className="text-xs text-[#8C8C8C] mb-2">
                {total} {category} found near {searchedDest}
              </p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {places.map((place) => (
                  <div
                    key={place.id}
                    className="group p-3 rounded-lg bg-[#F9F8F4] hover:bg-[#F5F1EB] transition-colors"
                    data-testid={`place-result-${place.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-[#1A1A1A] truncate">{place.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {place.cuisine && (
                            <span className="text-xs text-[#C8A85A]">{place.cuisine.replace(/;/g, ', ')}</span>
                          )}
                          {place.address && (
                            <span className="text-xs text-[#8C8C8C] truncate">{place.address}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          {place.phone && (
                            <span className="text-xs text-[#8C8C8C] flex items-center gap-1">
                              <Phone className="w-3 h-3" />{place.phone}
                            </span>
                          )}
                          {place.opening_hours && (
                            <span className="text-xs text-[#8C8C8C] flex items-center gap-1">
                              <Clock className="w-3 h-3" />{place.opening_hours}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        {place.website && (
                          <a
                            href={place.website}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 rounded hover:bg-[#EEE9E2]"
                            title="Website"
                          >
                            <ExternalLink className="w-3.5 h-3.5 text-[#C8A85A]" />
                          </a>
                        )}
                        {onAddToItinerary && (
                          <button
                            onClick={() => onAddToItinerary(place)}
                            className="p-1.5 rounded hover:bg-[#C8A85A]/10"
                            title="Add to itinerary"
                            data-testid={`add-place-${place.id}`}
                          >
                            <Plus className="w-3.5 h-3.5 text-[#C8A85A]" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : searchedDest ? (
            <div className="text-center py-6">
              <IconForCategory className="w-8 h-8 mx-auto text-[#8C8C8C] mb-2" />
              <p className="text-sm text-[#8C8C8C]">No {category} found near {searchedDest}</p>
              <p className="text-xs text-[#B0B0B0] mt-1">Try a different category or location</p>
            </div>
          ) : (
            <div className="text-center py-6">
              <Search className="w-8 h-8 mx-auto text-[#8C8C8C] mb-2" />
              <p className="text-sm text-[#8C8C8C]">Search for places near your destination</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DiscoverPlaces;
